import { Form, Spin } from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "../../../../assets/styles/InterbankPlacement.css";
import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import cnt from "../../../../services/definedConstant";
import { BOM } from "../../../../services/definedMethod";
import FeedbackModal from "../../../treasury/bond/components/FeedbackModal";
import { Util } from "../../../treasury/treasuryBill/helper/helper";
import {
  useGetProdTemplate,
  useGetTreasuryItem,
} from "../../../treasury/treasuryBill/hooks/useServerActions";
import StepsCard from "../../component/StepsCard";
import StepsComponent from "../../component/StepsComponent";
import SuccessModal from "../../component/SuccessModal";
import {
  apiConfig,
  labelConfig,
  staticConfig,
} from "../../contractbooking.config";
import { extractNumberFromText } from "../../helper/helper";
import {
  useGetCustomer,
  useGetCustomerAccounts,
  useGetTreasuryBillContract,
  useGetTreasuryBillContractTemplate,
  useSearchCustomer,
} from "../../hooks/useServerActions";
import ChargeDetails from "./components/ChargeDetails";
import ContractInformationForm from "./components/ContactInformationForm";
import InstrumentDetailForm from "./components/InstrumentDetailForm";
import InvestmentDetails from "./components/InvestmentDetails";
import MaturitySettings from "./components/MaturitySettings";
import PreviewForm from "./components/PreviewDetails";
import TaxAndRegulatoryForm from "./components/Tax&RegulatoryForm";

const EditTreasuryBill = ({ userData }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { session, username } = userData;
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [formData, setFormData] = useState();
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [chargeData, setChargeData] = useState(null);

  const selectedTreasuryBillItem =
    Form.useWatch("productId", form) ?? formData?.productId;
  const clientId = Form.useWatch("clientId", form);
  const allowRollover = Form.useWatch("allowRollover", form);
  const { prodTemplate, loading: treasuryProdTemplateLoading } =
    useGetProdTemplate(session);
  const { contractTemplate, loading: contractTemplateIsLoading } =
    useGetTreasuryBillContractTemplate(session);
  const { contract, loading: contractLoading } = useGetTreasuryBillContract(
    session,
    state.key
  );
  const { treasuryBillItem, loading: treasuryItemLoading } = useGetTreasuryItem(
    session,
    selectedTreasuryBillItem || contract?.product?.id
  );
  const {
    mutate,
    loading: searchLoading,
    data: searchData,
  } = useSearchCustomer(session);
  const { loading: customerLoading, data: customer } = useGetCustomer(
    session,
    searchData.entityId ?? contract?.client?.id
  );
  const { data: customerAccounts, loading: accountLoading } =
    useGetCustomerAccounts(session, customer?.id);

  const issueDate = Form.useWatch("issueDate", form) ?? formData?.issueDate;
  const faceValue = Form.useWatch("faceValue", form) ?? formData?.faceValue;
  const tenor = Form.useWatch("tenor", form) ?? formData?.tenor;
  const selectedTenorValue = extractNumberFromText(
    Util.mapFormFieldToLabel("tenorOptions", tenor, prodTemplate)
  );
  const maturityDate = issueDate
    ? dayjs(issueDate).add(selectedTenorValue, "day")
    : undefined;
  const productCreatedDate = dayjs(treasuryBillItem?.startDate);
  const minInvestment = treasuryBillItem?.minInvestment;

  const treasuryBillOptions = contractTemplate?.products?.filter(
    (item, i) => item?.status?.value === 300
  );
  const customerAccOptions = customerAccounts?.savingsAccounts
    ?.filter((item) => item?.currency?.code === treasuryBillItem?.currencyCode)
    .map((item) => ({
      value: item.id,
      label: `${item.productName} (${item.accountNo})`,
    }));
  const currencyOptions = prodTemplate?.currencies?.map((item) => ({
    value: item.value,
    label: item.label,
    symbol: item.symbol,
  }));
  const currency = currencyOptions?.find(
    (item) => item.value === treasuryBillItem?.currencyCode
  )?.symbol;
  const custodianOptions = prodTemplate?.custodians?.map((item) => ({
    value: item.value,
    label: item.label,
  }));

  // FUNCTIONS
  function handleSearch(searchId) {
    mutate(clientId).then(() => {
      // You can get data from here as well
      form.setFieldValue("linkedAccountId", null);
    });
  }
  function updateFormData(newData) {}
  function onFinish(newData) {
    setFormData({
      ...formData,
      ...newData,
    });
    setCurrent(current + 1);
  }

  function next() {
    const stepsUsingForm = [0, 1, 3, 4, 5]; // indices of steps

    if (stepsUsingForm.includes(current)) {
      form.submit(); // only those steps trigger onFinish
    } else {
      setCurrent(current + 1);
    }
  }

  function prev() {
    const prevStep = current - 1;
    setCurrent(prevStep);
    setCompletedSteps((prev) => {
      const updated = prev.filter((stepIndex) => stepIndex < prevStep);
      return updated;
    });
  }

  function handleSubmit() {
    delete formData.dateCreated;
    delete formData.instrument;
    delete formData.issuer;
    delete formData.maturityDate;
    delete formData.InterestAccrual;
    // delete formData.autoMaturityTransfer;

    const body = {
      ...formData,
      autoRollover: treasuryBillItem?.autoRollover ?? false,
      allowRollover: treasuryBillItem?.allowRollover ?? false,
      withholdTax: formData.withholdTax ?? false,
      kycValidated: formData.kycValidated ?? false,
      centralBankAuctionRefLinked:
        formData.centralBankAuctionRefLinked ?? false,
      autoMaturityTransfer: formData.autoMaturityTransfer ?? false,
      clientId: contract?.client?.id,
      tenor: selectedTenorValue,
      issueDate: BOM.DateTime(formData.issueDate, "only-date"),
      settlementDate: BOM.DateTime(formData.settlementDate, "only-date"),
      valueDate: BOM.DateTime(formData.valueDate, "only-date"),
      submittedOnDate: BOM.DateTime(dayjs(), "only-date"),
      locale: "en",
      dateFormat: "dd MMMM yyyy",
      method: "PUT",
      session,
    };

    setLoading(true);

    BOM.FetchReqAction(
      body,
      `${apiConfig.EditTreasuryBillContract}/${contract.id}`,
      (err, res) => {
        if (err) {
          setLoading(false);
          BOM.AlertMsg(cnt.DANGER, err?.errors?.defaultUserMessage);
        } else {
          setShowSuccessModal(true);
          setLoading(false);
        }
      }
    );
  }

  const handleClose = () => {
    setShowSuccessModal(false);
    setShowWarningModal(false);
  };
  function onCancel() {
    navigate("/contract-booking-treasury-bill", { replace: false });
  }

  React.useEffect(() => {
    form.setFieldsValue({
      ...formData,
      autoMaturityTransfer: contract?.autoMaturityTransfer,
      productId: contract?.product.id,
      // clientId: String(contract?.client.id),
      linkedAccountId: contract?.linkedAccount?.id,
      targetAccountId: contract?.targetAccount?.id,
      issueDate: dayjs(contract?.issueDate),
      faceValue: contract?.faceValue,
      purchasePrice: contract?.purchasePrice,
      yieldPercentage: contract?.yieldPercentage,
      interestType: contract?.interestType.value,
      settlementDate: dayjs(contract?.settlementDate),
      valueDate: dayjs(contract?.valueDate),
      maturityValue: contract?.maturityValue,
      withholdTax: contract?.withholdTax,
      centralBankReportingCode: contract?.centralBankReportingCode,
      amlScreening: contract?.amlScreening.value,
      kycValidated: contract?.kycValidated,
      centralBankAuctionRefLinked: contract?.centralBankAuctionRefLinked,
      auctionReference: contract?.auctionReference,
      allowRollover: contract?.allowRollover,
      autoRollover: contract?.autoRollover,
      maturityNarration: contract?.maturityNarration,
      // instrument: treasuryItem?.instrumentType?.id,
      // issuer: treasuryItem?.issuer?.id,
      // tenor: tenor ?? treasuryItem?.tenor?.value,
      // dateCreated: dayjs(treasuryItem?.startDate),
      // interestType: treasuryItem?.interestType?.value,
      // discountRate: treasuryItem?.discountRate,
    });
  }, [contract]);

  React.useEffect(() => {
    form.setFieldsValue({
      instrument: treasuryBillItem?.instrumentType?.id,
      issuer: treasuryBillItem?.issuer?.id,
      tenor: tenor ?? treasuryBillItem?.tenor?.value,
      dateCreated: dayjs(treasuryBillItem?.startDate),
      interestType: treasuryBillItem?.interestType?.value,
      discountRate: treasuryBillItem?.discountRate,
      custodian: treasuryBillItem?.custodian?.id,
    });

    setChargeData({
      commission: treasuryBillItem?.commission,
      setupCharge: treasuryBillItem?.setupCharge,
      redemptionCharge: treasuryBillItem?.redemptionCharge,
      transferCharge: treasuryBillItem?.transferCharge,
      allowTax: treasuryBillItem?.allowTax,
      taxGroup: treasuryBillItem?.taxGroup,
      setupCharges: treasuryBillItem?.setupCharges,
      redemptionCharges: treasuryBillItem?.redemptionCharges,
      transferCharges: treasuryBillItem?.transferCharges,
      allowBrokerage: treasuryBillItem?.allowBrokerage,
    });
  }, [treasuryBillItem]);

  React.useEffect(() => {
    form.setFieldsValue({
      maturityDate: maturityDate,
      settlementDate: dayjs(maturityDate).add(1, "days"),
      valueDate: dayjs(maturityDate),
    });
  }, [maturityDate]);

  React.useEffect(() => {
    const calculatedDiscountRate = contract?.discountRate / 100;
    const purchasePrice = Number(faceValue / (1 + calculatedDiscountRate));
    const daysToMaturity = selectedTenorValue;
    const yieldCalc1 = (faceValue - purchasePrice) / purchasePrice;
    const yieldCalc2 = (365 / daysToMaturity) * 100;
    const yieldResult = String(Number(yieldCalc1 * yieldCalc2).toFixed(2));
    form.setFieldsValue({
      maturityValue: faceValue ?? 0,
      purchasePrice: faceValue ? Number(purchasePrice).toFixed(2) : 0,
      yieldPercentage: faceValue ? yieldResult : 0,
    });
  }, [faceValue]);

  React.useEffect(() => {
    if (!issueDate) {
      form.setFieldsValue({
        issueDate: dayjs(),
      });
    }
  }, []);

  const stepsContent = [
    {
      title: "Contract Information",
      component: (
        <ContractInformationForm
          handleSearch={handleSearch}
          customer={customer}
          formData={formData}
          form={form}
          clientId={clientId}
          updateFormData={updateFormData}
          onFinish={onFinish}
          isEdit={true}
          // handleInputChange={handleInputChange}
          productOptions={treasuryBillOptions}
          custodianOptions={custodianOptions}
          customerAccOptions={customerAccOptions}
          accOptLoading={accountLoading}
          isProdDisabled={!customer}
          isAccDisabled={!customer || !treasuryBillItem}
          username={username}
        />
      ),
    },
    {
      title: "Instrument Details",
      component: (
        <InstrumentDetailForm
          form={form}
          formData={formData}
          onFinish={onFinish}
          prodTemplate={prodTemplate}
          productCreatedDate={productCreatedDate}
          // handleInputChange={handleInputChange}
        />
      ),
    },
    {
      title: "Charge Details",
      component: (
        <ChargeDetails chargeData={chargeData} currencySymbol={currency} />
      ),
    },
    {
      title: "Investment Details",
      component: (
        <InvestmentDetails
          formData={formData}
          form={form}
          onFinish={onFinish}
          prodTemplate={prodTemplate}
          minInvestment={minInvestment}

          // handleInputChange={handleInputChange}
        />
      ),
    },
    {
      title: "Tax & Regulatory",
      component: (
        <TaxAndRegulatoryForm
          onFinish={onFinish}
          formData={formData}
          form={form}
          contractTemplate={contractTemplate}
        />
      ),
    },
    {
      title: "Maturity Settings",
      component: (
        <MaturitySettings
          allowRollover={allowRollover}
          customerAccOptions={customerAccOptions}
          accOptLoading={accountLoading}
          onFinish={onFinish}
          formData={formData}
          form={form}
          contractTemplate={contractTemplate}
        />
      ),
    },
    {
      title: "Preview",
      component: (
        <PreviewForm
          username={username}
          contract={contract}
          customerAccounts={customerAccounts}
          customer={customer}
          product={treasuryBillItem}
          prodTemplate={prodTemplate}
          contractTemplate={contractTemplate}
          formData={formData}
          chargeData={chargeData}
          currencySymbol={currency}
        />
      ),
    },
  ];

  return (
    <MainLayout pageItems={staticConfig.editContractbookingTreasuryBill}>
      <TopContent
        pageTitle={labelConfig.manageContractBookingtTreasuryBill}
        createPageTitle={labelConfig.updateContractBookingtTreasuryBill}
      />

      <Spin
        spinning={
          loading ||
          searchLoading ||
          contractTemplateIsLoading ||
          customerLoading ||
          treasuryProdTemplateLoading ||
          contractLoading ||
          treasuryItemLoading
        }
        size="large"
      >
        <div
          className="contract-form-container"
          style={{
            display: "flex",
            paddingLeft: "44px",
            marginTop: "20px",
            height: "720px",
            widtth: "100%",
          }}
        >
          {/* Left side - Vertical steps */}
          <StepsComponent
            steps={stepsContent}
            current={current}
            completedSteps={completedSteps}
          />

          <StepsCard
            current={current}
            onGoBack={() => setShowWarningModal(true)}
            onNext={next}
            onPrev={prev}
            totalSteps={7}
            nextLabel={`Next - ${stepsContent[current + 1]?.title || ""}`}
            submitLabel={"Update contract"}
            onSubmit={handleSubmit}
          >
            {stepsContent[current].component}
          </StepsCard>

          {/* <SuccessModal visible={isModalVisible} onClose={() => setIsModalVisible(false)} /> */}
        </div>
      </Spin>
      <SuccessModal
        visible={showSuccessModal}
        // onClose={() => setIsModalVisible(false)}
        onClose={onCancel}
        successMessage={
          "Your Treasury Bill Contract has been updated successfully."
        }
        isCreateButton={false}
      />
      {/* <FeedbackModal message={"Your Contract has been updated successfully."} visible={showSuccessModal} type="success" onPrimaryAction={onCancel} primaryButtonText={"Done"} onClose={handleClose} /> */}
      <FeedbackModal
        visible={showWarningModal}
        type="warning"
        onPrimaryAction={onCancel}
        onSecondaryAction={handleClose}
        onClose={handleClose}
      />
    </MainLayout>
  );
};

// const SuccessModal = ({ visible, onClose }) => {
//   const { Title, Paragraph } = Typography;

//   return (
//     <Modal visible={visible} footer={null} closable={false} centered width={500}>
//       <div style={{ padding: "24px 0" }}>
//         <div
//           className=""
//           style={{
//             display: "flex",
//             justifyContent: "left",
//             flexDirection: "column",
//           }}
//         >
//           {/* <CheckCircleFilled style={{ fontSize: "24px", color: "#079455",backgroundColor:"#DCFAE6" }} /> */}
//           <div
//             style={{
//               color: "#079455",
//               backgroundColor: "#DCFAE6",
//               width: "48px",
//               height: "48px",
//               display: "flex",
//               justifyContent: "center",
//               alignItems: "center",
//               borderRadius: "50%",
//             }}
//           >
//             <CheckOutlined style={{ fontSize: "24px", color: "#079455" }} />
//           </div>
//           <Title level={3} style={{ marginTop: 16 }}>
//             Successful
//           </Title>
//         </div>
//         <Paragraph>Your Interbank Placement Contract have been created successfully.</Paragraph>
//         <div style={{ marginTop: 24, display: "flex", gap: "12px" }}>
//           <Button onClick={onClose} style={{ width: "100%", height: "44px" }}>
//             Close
//           </Button>
//           <Button
//             type="primary"
//             style={{
//               width: "100%",
//               backgroundColor: "#BA24D5",
//               color: "white",
//               height: "44px",
//             }}
//           >
//             Check Details
//           </Button>
//         </div>
//       </div>
//     </Modal>
//   );
// };

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

const mapDispatchToProps = {};
export default connect(mapStateToProps, mapDispatchToProps)(EditTreasuryBill);
